import { ApiService } from '~/services/api/api'
import type {
  AccessMe,
  OrganizationRoleChange,
  WorkspaceRoleChange,
} from '~/types/v2/Access'
import type { AssignableWorkspaceRole } from '~/constants/mappings/roles'
import type { SendOrganizationInvitesResult } from '~/types/Invite'

export interface InviteWorkspaceRole {
  workspaceId: string
  role: AssignableWorkspaceRole
}

// The /v2/ access routes (studio-api's `src/modules/v2/access/`). Each role
// route answers with the person's role afterwards, changed or not.
export class AccessService {
  private readonly apiService = new ApiService()

  // The signed-in person's roles and CASL rules.
  async getAccessMe() {
    const response = await this.apiService.get<AccessMe>('/v2/access/me')
    return response
  }

  async postOwner(organizationId: string, userId: string) {
    const response = await this.apiService.post<
      OrganizationRoleChange,
      { userId: string }
    >(`/v2/organizations/${organizationId}/owners`, { userId })
    return response
  }

  // The person becomes an organisation member.
  async deleteOwner(organizationId: string, userId: string) {
    const response = await this.apiService.delete<OrganizationRoleChange>(
      `/v2/organizations/${organizationId}/owners/${userId}`,
    )
    return response
  }

  // Org admin or member; never Owner (the Owner routes do that).
  async patchOrganizationRole(
    organizationId: string,
    userId: string,
    role: 'org_admin' | 'member',
  ) {
    const response = await this.apiService.patch<
      OrganizationRoleChange,
      { role: 'org_admin' | 'member' }
    >(`/v2/organizations/${organizationId}/users/${userId}/role`, { role })
    return response
  }

  async patchWorkspaceRole(
    workspaceId: string,
    userId: string,
    role: AssignableWorkspaceRole,
  ) {
    const response = await this.apiService.patch<
      WorkspaceRoleChange,
      { role: AssignableWorkspaceRole }
    >(`/v2/workspaces/${workspaceId}/users/${userId}/role`, { role })
    return response
  }

  // Invites people to the organisation with a role in each workspace. Like
  // the old route, it answers 200 with per-email buckets: a rejected email is
  // in `failed`, not an error.
  async postInvites(
    organizationId: string,
    emails: string[],
    workspaces: InviteWorkspaceRole[],
  ) {
    const response = await this.apiService.post<
      SendOrganizationInvitesResult,
      { emails: string[]; workspaces: InviteWorkspaceRole[] }
    >(`/v2/organizations/${organizationId}/invites`, { emails, workspaces })
    return response
  }
}
